import {forwardRef, useCallback, useEffect, useMemo} from 'react';
import {useTranslation} from 'react-i18next';
import {Check, Crown} from 'lucide-react';
import {Button, CheckboxCardList, Tag, cn} from '@chekinapp/ui';
import {sanitizeStringFromDashes, type Participant, type ShortGuest} from '@chekin/core';
import {getAge, getInitials, isAdult} from 'utils/common';
import {
  getConflictKeys,
  getDuplicateIdentity,
  getParticipantNameAndBirthDateIdentity,
  getParticipantNameAndDocumentIdentity,
  getRegisteredIdentities,
  pickDistinctParticipants,
} from './duplicateIdentity';

interface Props {
  participants: Participant[];
  value: Participant[];
  onChange: (selected: Participant[]) => void;
  guests?: ShortGuest[];
  leadId?: string;
}

const BULK_SELECT_THRESHOLD = 5;

const LIST_CLASS =
  'gap-2 overflow-visible rounded-none border-0 bg-transparent [box-shadow:none]';
const ROW_CLASS = cn(
  'rounded-[14px] border-t-0 bg-[var(--chekin-blue-surface-75)] hover:bg-[var(--chekin-blue-surface-100)]',
  'before:hidden [--radio-card-selected-bg:color-mix(in_srgb,var(--chekin-color-brand-blue)_10%,var(--chekin-color-white))]',
);
const AVATAR_CLASS =
  'rounded-[10px] bg-[var(--chekin-color-white)] text-[var(--chekin-color-brand-blue)] [box-shadow:inset_0_0_0_1px_var(--chekin-blue-200)]';
const SELECTED_AVATAR_CLASS =
  'bg-[var(--chekin-success-surface)] text-[var(--chekin-green-700)] [box-shadow:inset_0_0_0_1px_var(--chekin-success)]';

export const SelectGuestList = forwardRef<HTMLDivElement, Props>(
  ({guests, participants, value, onChange, leadId}, ref) => {
    const {t} = useTranslation();

    const filteredParticipants = useMemo(() => {
      const guestParticipantIdsSet = new Set(
        guests?.map(guest => guest.participant_id) ?? [],
      );

      return participants.filter(
        participant =>
          !guestParticipantIdsSet.has(sanitizeStringFromDashes(participant.id)),
      );
    }, [guests, participants]);

    const selectedIds = useMemo(
      () => new Set(value.map(participant => participant.id)),
      [value],
    );

    const takenKeys = useMemo(() => new Set(value.flatMap(getConflictKeys)), [value]);

    const registeredIdentities = useMemo(
      () => getRegisteredIdentities(guests, participants),
      [guests, participants],
    );

    const isAlreadyRegistered = useCallback(
      (participant: Participant) =>
        registeredIdentities.exact.has(getDuplicateIdentity(participant)) ||
        registeredIdentities.nameAndDocument.has(
          getParticipantNameAndDocumentIdentity(participant),
        ) ||
        registeredIdentities.nameAndBirthDate.has(
          getParticipantNameAndBirthDateIdentity(participant),
        ),
      [registeredIdentities],
    );

    const pickSelectable = useCallback(
      (candidates: Participant[]) =>
        pickDistinctParticipants(
          candidates.filter(participant => !isAlreadyRegistered(participant)),
        ),
      [isAlreadyRegistered],
    );

    const selectableParticipants = useMemo(
      () => pickSelectable(filteredParticipants),
      [pickSelectable, filteredParticipants],
    );

    useEffect(
      function dropSelectionsTheApiWouldReject() {
        const allowed = pickSelectable(value);

        if (allowed.length !== value.length) {
          onChange(allowed);
        }
      },
      [value, onChange, pickSelectable],
    );

    const options = [
      ...(guests ?? []).map(guest => ({
        value: `registered-${guest.id}`,
        label: guest.full_name,
        description: (
          <span className="flex items-center gap-1">
            <Check size={12} className="text-[var(--chekin-success-text)]" />
            {t('auth.already_registered')}
          </span>
        ),
        icon: getInitials(guest.full_name),
        iconClassName:
          'rounded-full bg-[var(--chekin-success-surface)] text-[var(--chekin-green-700)]',
        locked: true,
      })),
      ...filteredParticipants.map(participant => {
        const fullName = `${participant.name} ${participant.surname}`;
        const age = getAge(participant.birth_date);
        const isRegistered = isAlreadyRegistered(participant);
        const isDuplicateOfSelected =
          !selectedIds.has(participant.id) &&
          getConflictKeys(participant).some(key => takenKeys.has(key));
        const isBlocked = isRegistered || isDuplicateOfSelected;

        const describe = () => {
          if (isRegistered) return t('auth.already_registered_guest_hint');
          if (isDuplicateOfSelected) return t('auth.duplicate_guest_hint');

          const role = isAdult(participant.birth_date)
            ? t('adult')
            : t('auth.minor_with_age', {age});
          const documentEnding = participant.document_number?.slice(-4);

          return documentEnding
            ? `${role} · ${t('instant_check_in.document_ending', {
                digits: documentEnding,
              })}`
            : role;
        };

        return {
          value: participant.id,
          label:
            participant.id === leadId ? (
              <span className="flex flex-wrap items-center gap-2">
                {fullName}
                <Tag tone="info" size="xs">
                  <Crown size={11} />
                  {t('instant_check_in.lead_guest')}
                </Tag>
              </span>
            ) : (
              fullName
            ),
          description: describe(),
          icon: getInitials(fullName),
          iconClassName: cn(
            AVATAR_CLASS,
            selectedIds.has(participant.id) && SELECTED_AVATAR_CLASS,
          ),
          disabled: isBlocked,
        };
      }),
    ];

    const selectedValues = value.map(participant => participant.id);

    const handleChange = (nextValues: string[]) => {
      onChange(
        filteredParticipants.filter(participant => nextValues.includes(participant.id)),
      );
    };

    const showBulkSelect = filteredParticipants.length > BULK_SELECT_THRESHOLD;
    const areAllSelected =
      selectableParticipants.length > 0 && value.length === selectableParticipants.length;

    return (
      <div className="flex flex-col gap-2">
        {showBulkSelect && (
          <div className="flex items-center justify-between gap-3 px-0.5">
            <span className="text-[13px] font-semibold text-[var(--chekin-color-brand-navy)]">
              {t('auth.guests_selected_count', {
                selected: value.length,
                total: selectableParticipants.length,
              })}
            </span>
            <Button
              variant="link"
              type="button"
              className="h-auto text-[13px] font-semibold"
              onClick={() => onChange(areAllSelected ? [] : selectableParticipants)}
            >
              {areAllSelected ? t('auth.clear_selection') : t('auth.select_all')}
            </Button>
          </div>
        )}

        <CheckboxCardList
          ref={ref}
          options={options}
          value={selectedValues}
          onChange={handleChange}
          className={LIST_CLASS}
          optionClassName={ROW_CLASS}
        />
      </div>
    );
  },
);
